import { useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { Navigate, useLocation } from 'react-router-dom'
import markerSDK, { type MarkerReporter, type MarkerSdk } from '@marker.io/browser'
import { LoadingSpinner } from './LoadingSpinner'
import { useMe } from '../hooks/useMe'
import { applyBrandColors } from '../../utils/color'
import { applyAppIcon } from '../../utils/appIcon'
import { getAppEnv } from '../../lib/env'
import { isMarkerIoEnabled } from '../../lib/feedback-widgets'
import { captureIntendedLocation } from '../utils/session'

interface ProtectedRouteProps {
  children: React.ReactNode
}

const AUTHKIT_TIMEOUT_MS = 3000

// Marker.io's widget is a page-level singleton — one instance per page load,
// shared by every <ProtectedRoute> mount. Tracked outside the component so
// remounts, and StrictMode's double-invoked effects in dev, cannot start a
// second load while the first is still initialising.
//
// Seeded from `import.meta.hot.data` so Vite Fast Refresh does not reset the
// guard while the widget it loaded is still in the document — editing this file
// in dev would otherwise reproduce the duplicate-load crash this prevents. In a
// production build `import.meta.hot` is undefined and this is a plain
// module-scope value. Null means "not loaded yet, or the last attempt failed".
let markerWidget: Promise<MarkerSdk> | null =
  (import.meta.hot?.data.markerWidget as Promise<MarkerSdk> | undefined) ?? null

function trackMarkerWidget(widget: Promise<MarkerSdk> | null): void {
  markerWidget = widget
  if (import.meta.hot) {
    import.meta.hot.data.markerWidget = widget
  }
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth()
  const { data: me } = useMe()
  const location = useLocation()
  const [authKitTimedOut, setAuthKitTimedOut] = useState(false)

  // If AuthKit is stuck loading (e.g. missing refresh token), stop waiting after 3s
  useEffect(() => {
    if (!isLoading) {
      setAuthKitTimedOut(false)
      return
    }
    const timer = setTimeout(() => setAuthKitTimedOut(true), AUTHKIT_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isLoading])

  // Apply tenant branding once for all authenticated routes
  useEffect(() => {
    if (me?.tenant?.primary_color) {
      applyBrandColors({
        primaryColor: me.tenant.primary_color,
        navBackgroundColor: me.tenant.nav_background_color,
        navTextColor: me.tenant.nav_text_color,
        buttonTextColor: me.tenant.button_text_color,
      })
    }
  }, [
    me?.tenant?.primary_color,
    me?.tenant?.nav_background_color,
    me?.tenant?.nav_text_color,
    me?.tenant?.button_text_color,
  ])

  // Patch <head> so the browser favicon, iOS touch icon, PWA manifest
  // link and document title all follow the tenant's uploaded logo and
  // name. The manifest link is what lets Chrome Android's "Add to Home
  // Screen" flow pick up the tenant logo instead of falling back to a
  // letter tile — see `utils/appIcon.ts` for the platform matrix.
  useEffect(() => {
    if (!me?.tenant) return
    applyAppIcon({
      logoUrl: me.tenant.logo_url,
      tenantName: me.tenant.name,
      tenantSlug: me.tenant.slug,
    })
  }, [me?.tenant?.logo_url, me?.tenant?.name, me?.tenant?.slug])

  // Which feedback widget runs in which env is controlled by
  // src/lib/feedback-widgets.ts — edit the arrays there to flip.
  //
  // Loaded once per page load and never unloaded on route change. routes.tsx
  // mounts three sibling <ProtectedRoute> elements (engineer / no-access /
  // admin), so navigating between them remounts this component; a per-instance
  // guard let a second loadWidget() start while the previous widget was still
  // tearing down, and Marker's loadWidget() then called unload() on an
  // already-removed node — "Cannot read properties of null (reading 'remove')",
  // which the ErrorBoundary turned into a full-page "Something went wrong".
  //
  // `me` changes whenever the auth-me query is invalidated (profile edits,
  // checkout, cart postcode writes), so a changed name/email is pushed into the
  // live widget with setReporter() rather than by reloading it.
  useEffect(() => {
    if (!me) return
    if (!isMarkerIoEnabled(getAppEnv())) return
    const projectId = import.meta.env.VITE_MARKER_PROJECT_ID
    if (!projectId) return

    const reporter: MarkerReporter = {
      email: me.email,
      fullName: `${me.first_name} ${me.last_name}`.trim() || me.email,
    }

    if (markerWidget) {
      markerWidget
        .then(widget => widget.setReporter(reporter))
        .catch(() => { /* the load path below already reported the failure */ })
      return
    }

    // A QA feedback widget must never be able to blank the authenticated app,
    // so failures are contained here instead of reaching the ErrorBoundary.
    try {
      const pending = markerSDK.loadWidget({ project: projectId, reporter })
      trackMarkerWidget(pending)
      pending.catch((error: unknown) => {
        trackMarkerWidget(null)
        console.warn('Marker.io widget failed to load', error)
      })
    } catch (error) {
      trackMarkerWidget(null)
      console.warn('Marker.io widget failed to load', error)
    }
  }, [me])

  if (isLoading && !authKitTimedOut) {
    return <LoadingSpinner message="Authenticating..." />
  }

  // Check for authentication: either WorkOS user OR valid tokens in localStorage
  const hasStoredAuth = localStorage.getItem('access_token') || localStorage.getItem('refresh_token')

  if (!user && !hasStoredAuth) {
    captureIntendedLocation(location.pathname + location.search)
    return <Navigate to="/auth/login" replace />
  }

  return <>{children}</>
}
